// ABOUTME: Comparison-period bar for chart date types — RhFilter-style chips you add/remove to overlay on
// ABOUTME: the graph: simple periods, a date picker, and living recurring rules (e.g. first day of each month).
import { Box, ButtonBase, useTheme } from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { KeyboardArrowDown } from "@mui/icons-material";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { tokens } from "../../../../theme";
import { chartDateOptions } from "../chartFilterEnums";
import { DropdownBaseV2 } from "../../../theme/dropdowns/Dropdown";
import PopoverModal from "../../../theme/Modal/PopoverModal";
import ShDateRangePicker from "../../../theme/ShDateRangePicker";
import NumberInput from "../../../theme/inputs/NumberInput";
import Text from "../../../theme/text/Text";
import Subheading from "../../../theme/text/Subheading";

const UNIT_ORDER = ["day", "week", "month", "quarter", "year"];
// Parent cadences a recurring rule can step over — only units LARGER than the dataset's own unit.
const parentOptionsFor = (unit) => UNIT_ORDER.slice(UNIT_ORDER.indexOf(unit) + 1);

const fmtDay = (s, lang) =>
    s ? new Date(s).toLocaleDateString(lang, { day: "numeric", month: "short" }) : "";

// Human label for one comparison line: show the actual resolved period (its date range) once the backend
// returns it — always with its year so a legend mixing years stays unambiguous — and fall back to the
// preset name while loading.
export const compareLabel = (spec, range, t, lang) => {
    if (range?.start && range?.end) {
        const startYear = new Date(range.start).getFullYear();
        return `${fmtDay(range.start, lang)} – ${fmtDay(range.end, lang)} ${startYear}`;
    }
    if (!spec) return "";
    if (spec.type === "lastYear") return t("previous_year");
    if (spec.type === "offset") return spec.n === 1 ? t("previous_period") : `${spec.n} ${t("periods_ago")}`;
    if (spec.type === "date") return fmtDay(spec.date, lang);
    return "";
};

// One-line summary of a recurring rule, e.g. "Eerste dag · van elke maand · 4". The slot is the dataset unit.
export const recurringSummary = (spec, unit, t) => {
    const pick = spec.pick === "nth" ? `${spec.nth || 1}e`
        : (spec.pick === "last" ? t("slot_last") : spec.pick === "each" ? t("slot_each") : t("slot_first"));
    const slot = t(`unit_${unit || "week"}`).toLowerCase();
    const every = t(`unit_${spec.every || "month"}`).toLowerCase();
    return `${pick} ${slot} · ${t("of_each")} ${every} · ${spec.count || 4}`;
};

const SettingRow = ({ label, children }) => (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 3 }}>
        <Text noBreak>{label}</Text>
        {children}
    </Box>
);

export const RhRollingCompare = ({ currentReport, setCurrentReport, rolling }) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const isDark = theme.palette.mode === "dark";
    const [addingDate, setAddingDate] = useState(false);
    const [editing, setEditing] = useState(null); // { idx, anchorEl } for the recurring-rule editor
    const addBtnRef = useRef(null);

    const unit = currentReport?.date?.unit;
    const isRolling = currentReport?.date?.type === chartDateOptions.ROLLING;
    const parentOpts = isRolling && unit ? parentOptionsFor(unit) : [];

    const specs = currentReport?.date?.compare ?? [];
    const comparisons = rolling?.series?.comparisons ?? [];

    const setSpecs = (next) => {
        setCurrentReport(prev => ({
            ...prev,
            date: { ...prev.date, compare: typeof next === "function" ? next(prev?.date?.compare ?? []) : next },
        }));
    };
    const updateSpec = (i, patch) => setSpecs(cur => cur.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
    const removeSpec = (i) => setSpecs(cur => cur.filter((_, idx) => idx !== i));

    // A new recurring rule borrows the dataset's unit as its slot and steps over the next-larger cadence.
    const makeRecurring = () => ({ type: "recurring", pick: "first", every: parentOpts.includes("month") ? "month" : parentOpts[0], count: 4 });

    const presets = [
        { label: t("previous_period"), add: () => setSpecs(cur => [...cur, { type: "offset", n: 1 }]) },
        { label: t("previous_4_periods"), add: () => setSpecs(cur => [...cur, { type: "offset", n: 1 }, { type: "offset", n: 2 }, { type: "offset", n: 3 }, { type: "offset", n: 4 }]) },
        { label: t("previous_year"), add: () => setSpecs(cur => [...cur, { type: "lastYear" }]) },
        { label: t("pick_specific_period"), add: () => setAddingDate(true) },
        ...(parentOpts.length ? [{ label: t("recurring_period"), add: () => { setSpecs(cur => [...cur, makeRecurring()]); setEditing({ idx: specs.length, anchorEl: addBtnRef.current }); } }] : []),
    ];

    const PICK_OPTS = [
        { value: "first", label: t("slot_first") },
        { value: "last", label: t("slot_last") },
        { value: "nth", label: t("slot_nth") },
        { value: "each", label: t("slot_each") },
    ];
    const everyOpts = parentOpts.map(u => ({ value: u, label: t(`unit_${u}`) }));

    // Label for a chip: a recurring rule shows its summary; everything else shows its resolved date range.
    const chipLabel = (spec, i) =>
        spec.type === "recurring" ? recurringSummary(spec, unit, t) : compareLabel(spec, comparisons.find(c => c.specIndex === i), t, i18n.language);

    const renderDropdown = (options, value, onChange) => {
        const idx = options.findIndex(o => o.value === value);
        return (
            <DropdownBaseV2 options={options} displayName="label" selected={idx} onChange={(i) => onChange(options[i].value)} closeOnSelection disallowDeselection>
                {({ onClick }) => (
                    <ButtonBase
                        onClick={onClick}
                        sx={{
                            height: 32, borderRadius: 5, px: 2, gap: 1,
                            background: isDark ? colors.grey[300] : colors.grey[100],
                            color: colors.txt["primary"], fontWeight: 500, fontSize: 14,
                        }}
                    >
                        {options[idx]?.label}
                        <KeyboardArrowDown fontSize="small" />
                    </ButtonBase>
                )}
            </DropdownBaseV2>
        );
    };

    const editingSpec = editing ? specs[editing.idx] : null;

    return (
        <Box
            sx={{
                minHeight: 40,
                p: 0.5,
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
                background: isDark ? colors.grey[300] : colors.grey[0],
                border: `1px solid ${colors.grey[100]}`,
                boxShadow: `0px 2px 4px ${colors.grey[200]}`,
                borderRadius: 5,
                "@media screen and (max-width: 768px)": { display: "none" },
            }}
        >
            <Text sx={{ pl: 1.5 }}>{t("compare_with")}</Text>

            {specs.map((spec, i) => {
                const isRecurring = spec.type === "recurring";
                return (
                    <Box
                        key={i}
                        onClick={isRecurring ? (e) => setEditing({ idx: i, anchorEl: e.currentTarget }) : undefined}
                        sx={{
                            pl: 1.5,
                            pr: 0.5,
                            height: 32,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            flexShrink: 0,
                            cursor: isRecurring ? "pointer" : "default",
                            background: isRecurring
                                ? (isDark ? colors.blue[100] : colors.primary[100])
                                : (isDark ? colors.grey[600] : colors.grey[100]),
                            border: isRecurring ? `1px solid ${isDark ? colors.blue[400] : colors.primary[400]}` : "none",
                            borderRadius: 4,
                        }}
                    >
                        <Text noBreak>{chipLabel(spec, i)}</Text>
                        <Box
                            onClick={(e) => { e.stopPropagation(); removeSpec(i); }}
                            sx={{
                                display: "flex", alignItems: "center", justifyContent: "center",
                                height: 24, width: 24, flexShrink: 0, borderRadius: 4, cursor: "pointer",
                                color: colors.txt["primary"],
                                background: isDark ? colors.grey[300] : colors.grey[0],
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </Box>
                    </Box>
                );
            })}

            {addingDate &&
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                    <ShDateRangePicker
                        size={32}
                        select={(value) => {
                            const date = value[0]?.formattedStartDate;
                            if (date) setSpecs(cur => [...cur, { type: "date", date }]);
                            setAddingDate(false);
                        }}
                        explicit
                    />
                    <Box
                        onClick={() => setAddingDate(false)}
                        sx={{
                            display: "flex", alignItems: "center", justifyContent: "center",
                            height: 32, width: 32, flexShrink: 0, borderRadius: 4, cursor: "pointer",
                            color: colors.txt["secondary"],
                            background: isDark ? colors.grey[600] : colors.grey[100],
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </Box>
                </Box>
            }

            {/* Add a comparison period — presets, a specific period, or a recurring rule */}
            {!addingDate &&
                <DropdownBaseV2 options={presets.map(p => p.label)} selected={-1} onChange={(idx) => presets[idx]?.add()} closeOnSelection resetOnSelection>
                    {({ onClick }) => (
                        <ButtonBase
                            ref={addBtnRef}
                            onClick={onClick}
                            sx={{
                                width: 32, height: 32, flexShrink: 0, borderRadius: 4,
                                background: colors.txt["primary"],
                                color: isDark ? colors.txt["primaryDark"] : colors.txt["light"],
                            }}
                        >
                            <AddOutlinedIcon fontSize="small" />
                        </ButtonBase>
                    )}
                </DropdownBaseV2>
            }

            {/* Recurring-rule editor — reads as a sentence: "Eerste dag / van elke maand / laatste 4" */}
            <PopoverModal
                open={!!editing}
                anchorEl={editing?.anchorEl}
                onClose={() => setEditing(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                triangle={{ position: "center", color: isDark ? colors.grey[200] : colors.grey[0] }}
            >
                {editingSpec &&
                    <Box sx={{ p: 3, pb: 2, minWidth: 300, background: isDark ? colors.grey[200] : colors.grey[0] }}>
                        <Subheading sx={{ pb: 2 }}>{t("recurring_title")}</Subheading>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                {renderDropdown(PICK_OPTS, editingSpec.pick || "first", (v) => updateSpec(editing.idx, { pick: v }))}
                                {editingSpec.pick === "nth" &&
                                    <NumberInput height={32} min={1} max={31} value={editingSpec.nth || 1} change={(e) => updateSpec(editing.idx, { nth: e })} />
                                }
                                <Box sx={{ height: 32, display: "flex", alignItems: "center", px: 2, borderRadius: 5, background: isDark ? colors.grey[300] : colors.grey[100], color: colors.txt["primary"], fontSize: 14, fontWeight: 500 }}>
                                    {t(`unit_${unit}`)}
                                </Box>
                            </Box>
                            <SettingRow label={t("of_each")}>
                                {renderDropdown(everyOpts, editingSpec.every || everyOpts[0]?.value, (v) => updateSpec(editing.idx, { every: v }))}
                            </SettingRow>
                            <SettingRow label={t("last_n")}>
                                <NumberInput height={32} min={1} max={24} value={editingSpec.count || 4} change={(e) => updateSpec(editing.idx, { count: e })} />
                            </SettingRow>
                        </Box>
                    </Box>
                }
            </PopoverModal>
        </Box>
    );
};
